/**
 * Shared file-attachment helpers for the Projects surfaces.
 *
 * Originally lived inline in ``ProjectChat``; extracted so the
 * landing composer, the per-project chat, and the desktop overlay
 * all validate / cap staged files identically before uploading them
 * via ``POST /projects/{id}/attachments`` (see ``uploadStagedFiles``).
 */

import {
  FILE_UPLOAD_ERROR_CODES,
  type FileUploadRejection,
} from '@heidi/design-system/file-upload';
import {
  AUDIO_EXTENSION_TO_MIME_TYPE,
  AUDIO_FILE_EXTENSIONS,
  AUDIO_MAX_SIZE_BYTES,
  AUDIO_MIME_TYPES,
  CHAT_ATTACHMENTS_ACCEPT_FORMATS,
  CHAT_ATTACHMENTS_ALLOWED_EXTENSIONS,
  CHAT_ATTACHMENTS_ALLOWED_MIME_TYPES,
  normalizePastedFile,
} from '@heidi/utils';

/** Work-only media extensions beyond the shared audio upload set. */
export const PROJECT_MEDIA_FILE_EXTENSIONS = [
  ...AUDIO_FILE_EXTENSIONS,
  '.mov',
  '.webm',
] as const;

/** Work-only media MIME types (documents/images stay on the chat allow-list). */
export const PROJECT_MEDIA_MIME_TYPES = [
  ...AUDIO_MIME_TYPES,
  'video/mp4',
  'video/quicktime',
  'video/webm',
] as const;

/** File-picker accept value for Work chat attachments. */
export const PROJECT_ATTACHMENTS_ACCEPT_FORMATS = [
  CHAT_ATTACHMENTS_ACCEPT_FORMATS,
  ...PROJECT_MEDIA_FILE_EXTENSIONS,
  ...PROJECT_MEDIA_MIME_TYPES,
].join(',');

/** Re-export Evidence/chat allow-lists plus Work-only audio/video. */
export const PROJECT_ATTACHMENTS_ALLOWED_EXTENSIONS = new Set([
  ...CHAT_ATTACHMENTS_ALLOWED_EXTENSIONS,
  ...PROJECT_MEDIA_FILE_EXTENSIONS.map((ext) => ext.replace('.', '')),
]);

export const PROJECT_ATTACHMENTS_ALLOWED_MIME_TYPES = new Set([
  ...CHAT_ATTACHMENTS_ALLOWED_MIME_TYPES,
  ...PROJECT_MEDIA_MIME_TYPES,
]);

/** Whether a file is audio/video for Work-chat transcription uploads. */
export const isProjectMediaAttachment = (file: File): boolean => {
  const type = file.type.toLowerCase();
  if (type.startsWith('audio/') || type.startsWith('video/')) {
    return true;
  }
  const lastDot = file.name.lastIndexOf('.');
  if (lastDot <= 0) {
    return false;
  }
  const extension = file.name.slice(lastDot).toLowerCase();
  return extension in AUDIO_EXTENSION_TO_MIME_TYPE;
};

export const maxBytesForProjectAttachment = (file: File): number =>
  isProjectMediaAttachment(file) ? AUDIO_MAX_SIZE_BYTES : MAX_ATTACHMENT_BYTES;

export const maxMegabytesForProjectAttachment = (file: File): number =>
  maxBytesForProjectAttachment(file) / (1024 * 1024);

/** Whether a picked file matches the Work/Evidence attachment allow-list. */
export const isProjectAttachmentAllowed = (file: File): boolean => {
  const lastDot = file.name.lastIndexOf('.');
  const hasExtension = lastDot > 0;
  if (!hasExtension) {
    return PROJECT_ATTACHMENTS_ALLOWED_MIME_TYPES.has(file.type.toLowerCase());
  }
  const extension = file.name.slice(lastDot + 1).toLowerCase();
  return PROJECT_ATTACHMENTS_ALLOWED_EXTENSIONS.has(extension);
};

/** Client-side label for unsupported-type toasts (kept short; no PHI in filenames). */
export const PROJECT_ATTACHMENTS_FORMATS_LABEL =
  'documents, spreadsheets, images, audio/video (MP3, MP4, …), XML, or plain text';

/** Per-file size cap. Matches Evidence's drop-zone. */
export const MAX_ATTACHMENT_MB = 10;
export const MAX_ATTACHMENT_BYTES = MAX_ATTACHMENT_MB * 1024 * 1024;
/** Per-message attachment count cap. Matches Evidence's drop-zone. */
export const MAX_ATTACHMENT_COUNT = 10;

/** Short uppercase file-type label for a chip ("PDF", "DOCX", …).
 *  Falls back to "FILE" for extensionless names so the chip is never
 *  blank. */
export const formatFileType = (filename: string): string => {
  const lastDot = filename.lastIndexOf('.');
  if (lastDot === -1 || lastDot === filename.length - 1) return 'FILE';
  return filename.slice(lastDot + 1).toUpperCase();
};

/**
 * Validate + cap freshly-picked files against an existing staged set.
 * Accepts a ``FileList`` (file picker / drop) or ``File[]`` (clipboard
 * paste via ``useChatPaste``). Returns the next staged array (capped)
 * plus an optional problem the caller should surface as a toast. Pure
 * so it's unit-testable without a DOM.
 *
 * ``maxCount`` defaults to ``MAX_ATTACHMENT_COUNT`` (per-message). The
 * landing composer passes a higher cap for first-turn project create.
 */
export interface MergeAttachmentsResult {
  next: File[];
  error?: { title: string; description: string };
}

export interface MergePickedFilesOptions {
  maxCount?: number;
  /**
   * How to treat files over ``MAX_ATTACHMENT_BYTES``:
   * - ``reject-all`` (default, chat composer): drop the whole pick and
   *   toast a generic size message (no filenames — they can contain PHI).
   * - ``skip`` (landing composer): keep valid files, drop only the
   *   oversized ones, and toast a count with no filenames.
   */
  oversizedPolicy?: 'reject-all' | 'skip';
  /**
   * Dropzone already rejected these files. Format the toast from its error
   * codes and leave the staged set unchanged — re-validating the files would
   * rename an extensionless image and then treat it as allowed.
   */
  dropRejections?: readonly FileUploadRejection[];
}

const formatDropRejections = (
  rejections: readonly FileUploadRejection[]
): MergeAttachmentsResult['error'] | undefined => {
  if (rejections.length === 0) return undefined;

  const invalidTypeCount = rejections.filter((rejection) =>
    rejection.errors.some(
      (error) => error.code === FILE_UPLOAD_ERROR_CODES.FILE_INVALID_TYPE
    )
  ).length;
  if (invalidTypeCount > 0) {
    return {
      title: 'Unsupported file type',
      description: `${invalidTypeCount} file${invalidTypeCount === 1 ? '' : 's'} couldn't be attached. Work accepts ${PROJECT_ATTACHMENTS_FORMATS_LABEL}.`,
    };
  }

  const tooLarge = rejections.find((rejection) =>
    rejection.errors.some(
      (error) => error.code === FILE_UPLOAD_ERROR_CODES.FILE_TOO_LARGE
    )
  );
  if (tooLarge) {
    return {
      title: 'File too large',
      description: `A file exceeds the ${maxMegabytesForProjectAttachment(tooLarge.file)} MB per-file limit.`,
    };
  }

  const tooManyFiles = rejections.some((rejection) =>
    rejection.errors.some(
      (error) => error.code === FILE_UPLOAD_ERROR_CODES.TOO_MANY_FILES
    )
  );
  if (tooManyFiles) {
    return {
      title: 'Too many attachments',
      description: `Up to ${MAX_ATTACHMENT_COUNT} files; extra files were dropped.`,
    };
  }

  return undefined;
};

export const mergePickedFiles = (
  existing: readonly File[],
  picked: FileList | readonly File[] | null,
  options?: MergePickedFilesOptions
): MergeAttachmentsResult => {
  if (options?.dropRejections) {
    return {
      next: [...existing],
      error: formatDropRejections(options.dropRejections),
    };
  }
  if (!picked || picked.length === 0) return { next: [...existing] };
  // FileList and readonly File[] are both ArrayLike<File>; cast keeps
  // typescript-eslint from widening Array.from to any[].
  // Clipboard screenshots often arrive as "image" with no extension. Paste
  // already renames those; a drop or a file-picker pick of the same file
  // has to as well, or the upload endpoint rejects the name.
  const incoming: File[] = Array.from(picked as ArrayLike<File>).map(normalizePastedFile);
  const maxCount = options?.maxCount ?? MAX_ATTACHMENT_COUNT;
  const oversizedPolicy = options?.oversizedPolicy ?? 'reject-all';

  const disallowed = incoming.filter((file) => !isProjectAttachmentAllowed(file));
  let accepted = incoming.filter((file) => isProjectAttachmentAllowed(file));
  let typeError: MergeAttachmentsResult['error'];
  if (disallowed.length > 0) {
    typeError = {
      title: 'Unsupported file type',
      description: `${disallowed.length} file${disallowed.length === 1 ? '' : 's'} couldn't be attached. Work accepts ${PROJECT_ATTACHMENTS_FORMATS_LABEL}.`,
    };
    if (accepted.length === 0) {
      return { next: [...existing], error: typeError };
    }
  }

  let oversizedError: MergeAttachmentsResult['error'];

  if (oversizedPolicy === 'skip') {
    const beforeSizeFilter = accepted.length;
    accepted = accepted.filter((f) => f.size <= maxBytesForProjectAttachment(f));
    const sizeRejectedCount = beforeSizeFilter - accepted.length;
    if (sizeRejectedCount > 0) {
      oversizedError = {
        title: 'Attachment too large',
        description: `${sizeRejectedCount} file${sizeRejectedCount === 1 ? '' : 's'} exceeded the per-file size limit and ${sizeRejectedCount === 1 ? 'was' : 'were'} skipped.`,
      };
    }
    if (accepted.length === 0) {
      return { next: [...existing], error: typeError ?? oversizedError };
    }
  } else {
    const tooLarge = accepted.find((f) => f.size > maxBytesForProjectAttachment(f));
    if (tooLarge) {
      return {
        next: [...existing],
        error: {
          title: 'File too large',
          description: `A file exceeds the ${maxMegabytesForProjectAttachment(tooLarge)} MB per-file limit.`,
        },
      };
    }
  }

  const merged: File[] = [...existing, ...accepted];
  if (merged.length > maxCount) {
    return {
      next: merged.slice(0, maxCount),
      error: typeError ??
        oversizedError ?? {
          title: 'Too many attachments',
          description: `Up to ${maxCount} files; extra files were dropped.`,
        },
    };
  }
  return { next: merged, error: typeError ?? oversizedError };
};
